import React, { useCallback, useEffect, useState, useRef } from 'react'
import axios, { CancelTokenSource } from 'axios'
import DataSource from 'devextreme/data/data_source'
import { useSearchParams, Link, useLocation } from 'react-router-dom'
import CustomStore from 'devextreme/data/custom_store'
import Button from 'devextreme-react/button'
import notify from '../../utils/notify'
import useBeforeUnload from '../../hooks/useBeforeUnload'
import { useModelDuplicateGrid } from '../../hooks/useModelDuplicateGrid'
import usePopupAutoFillCascade from '../../hooks/usePopupAutoFillCascade'
import { useAuth } from '../../contexts/auth'
import { canEditField, isHiddenField, canDeleteView, canInsertingView, canExportView } from '../../utils/permissions'
import DataGrid, {
	Column,
	Toolbar,
	Item,
	Grouping,
	GroupPanel,
	ColumnChooser,
	Selection,
	Scrolling,
	Editing,
	Popup as EditingPopup,
	Form,
	Paging,
	Lookup,
	FilterRow,
	HeaderFilter,
	MasterDetail,
	Export,
	Summary,
	GroupItem,
	FilterPanel,
	RequiredRule,
	KeyboardNavigation,
} from 'devextreme-react/data-grid'
import { Item as FormItem } from 'devextreme-react/form'
import {
	loadData,
	insertData,
	editData,
	deleteData,
	fixDataType,
	handleOptionChange,
	handleExporting,
	isNotEmpty,
	renderUrl,
	filterStore,
	lookupStore,
	minifiyUrl,
} from '../../utils/model-data'
import {
	Detail,
	Map,
	Row,
	MultipleSelect,
	MultipleSelectCellDisplay,
	BadgeFieldsCellDisplay,
	confirmDialog,
	getConditionalConfirm,
	showConditionalConfirmWithApi,
	showInsertWithPrecheck,
	AuditTrail,
	UploadField,
	MultipleUpload,
	SaveFilterPopup,
	ClickableField,
	JsonEditor,
	parseJsonValue,
} from '../../components'
import { ClickablePopupHost, type ClickablePopupHostHandle } from '../../components/clickable-popup/ClickablePopupHost'
import { cellWithHintText, renderClickablePopupCell } from '../../components/clickable-popup/columnRender'
import { Popup } from 'devextreme-react/popup'
import { LinkPermit, DataGridPopup, ModelPopup, BulkActionPopup, ModelGridPopup } from '../../popups'
import { saveAs } from 'file-saver'
import { useModelHotkeys } from '../../hooks/useModelHotkeys'
import { useFormUsage } from '../../hooks/useFormUsage'
import { useDocExportProgress } from '../../hooks/useDocExportProgress'
interface DynamicLookup {
	[key: string]: Array<{ value: string; text: string }>
}

interface SortCriteria {
	field: string
	order: 'asc' | 'desc'
}

interface DynamicLock {
	field: string
	condition: string
	value: any
	fields?: string[]
}

interface ButtonConfig {
	id?: string
	text: string
	icon: string
	visible?: boolean
	model?: string
	filter?: any
	toggle?: string
	url?: string
	blank?: boolean
	popup?: string
	title?: string
	args?: any
	email?: any
	doc?: any
	export?: any
	exec?: any
	execute?: string
	addRow?: { copy?: string[]; values?: Record<string, unknown> }
	successMessage?: string
}

export default function Model() {
	const [searchParams] = useSearchParams()
	const location = useLocation()
	const folder = searchParams.get('folder')
	const model = searchParams.get('model')
	let _defaultFilter = atob(searchParams.get('filter') ?? '')
	_defaultFilter = _defaultFilter ? JSON.parse(_defaultFilter) : ''
	const isDashboard = location.pathname.includes('/dashboard')

	useFormUsage({
		folder: folder || '',
		model: model || '',
		dashboard: isDashboard,
		filter: searchParams.get('filter') || null,
	})

	const [key, setKey] = useState<any>(null)
	const [fields, setFields] = useState<any[]>([])
	const [detail, setDetail] = useState<any>([])
	const [buttons, setButtons] = useState<ButtonConfig[]>([])
	const [lookups, setLookups] = useState<Record<string, Record<string, string>>>({})
	const [lookupHeaderFilterAsText, setLookupHeaderFilterAsText] = useState<string[]>([])
	const [filterLookups, setFilterLookups] = useState([])
	const [defaultFilter, setDefaultFilter] = useState<any>(_defaultFilter)
	const [dynamicLookups, setDynamicLookups] = useState<DynamicLookup>({})
	const [dynamicLookupServerSearch, setDynamicLookupServerSearch] = useState<string[]>([])
	const [dynamicLookupHintText, setDynamicLookupHintText] = useState<Record<string, string>>({})
	const [dynamicLookupClearDependencies, setDynamicLookupClearDependencies] = useState<Record<string, string[]>>({})
	const [autoFillOnInsert, setAutoFillOnInsert] = useState<string[]>([])
	const [dynamicFilterLookups, setDynamicFilterLookups] = useState<DynamicLookup>({})
	const [multipleLookups, setMultipleLookups] = useState<any>([])
	const [sizes, setSizes] = useState([])
	const [locks, setLocks] = useState<boolean | []>([])
	const [locksEdit, setLocksEdit] = useState<[]>([])
	const [dynamicLocks, setDynamicLocks] = useState<DynamicLock[] | DynamicLock | null>(null)
	const [sort, setSort] = useState<SortCriteria>()
	const [inserting, setInserting] = useState<boolean>(false)
	const [updating, setUpdating] = useState<boolean>(false)
	const [bulkUpdating, setBulkUpdating] = useState<boolean>(false)
	const [duplicating, setDuplicating] = useState<boolean>(false)
	const [duplicateFields, setDuplicateFields] = useState<string[] | null>(null)
	const [multiSelect, setMultiSelect] = useState<boolean>(false)
	const [buttonPopupShow, setButtonPopupShow] = useState(false)
	const [buttonArgs, setButtonArgs] = useState<[]>([])
	const [deleting, setDeleting] = useState<boolean>(false)
	const [grouping, setGrouping] = useState(0)
	const [urls, setUrls] = useState([])
	const [map, setMap] = useState<any>([])
	const [showMap, setShowMap] = useState(false)
	const [showRow, setShowRow] = useState(false)
	const [showMultipleUpload, setShowMultipleUpload] = useState('')
	const [showJsonEditor, setShowJsonEditor] = useState('')
	const [showClickableField, setShowClickableField] = useState<any>(null)
	const [dataSource, setDataSource] = useState<any>()
	const [dataFilter, setDataFilter] = useState<string | null>(null)
	const [totalCount, setTotalCount] = useState<number | null>(null)
	const [customLabel, setCustomLabel] = useState<string | null>(null)
	const [noDataText, setNoDataText] = useState<string | undefined>()
	const [removed, setRemoved] = useState<number>(0)
	const [modelText, setModelText] = useState('')
	const [height, setHeight] = useState(0)
	const [selectedRow, setSelectedRow] = useState<[]>([])
	const [rowColors, setRowColors] = useState<[]>([])
	const [cellColors, setCellColors] = useState<[]>([])
	const [mapColorFields, setMapColorFields] = useState([])
	const [percentagesFields, setPercentagesFields] = useState([])
	const [badgeFields, setBadgeFields] = useState<string[]>([])
	const [clickableFields, setClickableFields] = useState([])
	const [clickablePopups, setClickablePopups] = useState<
		Record<string, { colors?: Record<string, { background: string; color?: string }>; only?: string[] }>
	>({})
	const clickablePopupHost = useRef<ClickablePopupHostHandle>(null)
	const [hintText, setHintText] = useState<Record<string, string>>({})
	const [fileUploadFields, setFileUploadFields] = useState([])
	const [multipleUploadFields, setMultipleUploadFields] = useState([])
	const [jsonFields, setJsonFields] = useState([])
	const [autocompleteFields, setAutocompleteFields] = useState<string[]>([])
	const [exportWithPhotosOrFiles, setExportWithPhotosOrFiles] = useState(false)
	const [valueColors, setValueColors] = useState<any>([])
	const [detailFieldsSettings, setDetailFieldsSettings] = useState<any[]>([])
	const [detailFieldsSizes, setDetailFieldsSizes] = useState<any>({})
	const [detailFields, setDetailFields] = useState<any>({})
	const [clipboard, setClipboard] = useState<any>(null)
	const [editorMode, setEditorMode] = useState<boolean>(false)
	const [editMode, setEditMode] = useState<'cell' | 'popup'>('cell')
	const [popupTitle, setPopupTitle] = useState<string | null>(null)
	const [popupInsertOnly, setPopupInsertOnly] = useState<boolean>(false)
	const [popupInsertVisible, setPopupInsertVisible] = useState<string[]>([])
	const [fieldPlaceholders, setFieldPlaceholders] = useState<Record<string, string>>({})
	const [isNewRow, SetIsNewRow] = useState(false)
	const isNewRowRef = useRef(false)
	const syncNewRow = (value: boolean) => {
		isNewRowRef.current = value
		if (!editorMode) SetIsNewRow(value)
	}
	const autoFillOverlayRef = useRef<HTMLDivElement | null>(null)
	const { autoFillInProgressRef, runAutoFillCascade, loadDynamicLookupBatch } = usePopupAutoFillCascade({
		folder,
		model,
		editMode,
		isNewRowRef,
		onAutoFillLockChange: locked => {
			const el = autoFillOverlayRef.current
			if (!el) return
			el.style.display = locked ? 'block' : 'none'
		},
	})
	const [hovers, setHovers] = useState<any>([])
	const [showAuditTrail, setShowAuditTrail] = useState(false)
	const [auditData, setAuditData] = useState<any>([])
	const [auditField, setAuditField] = useState<string>('')
	const [filterPopupVisible, setFilterPopupVisible] = useState(false)
	const [exportLockedTime, setExportLockedTime] = useState<number | null>(null)
	const [conditionalConfirmDialog, setConditionalConfirmDialog] = useState<any[]>([])
	const [insertConfirmDialog, setInsertConfirmDialog] = useState<{
		title: string
		message: string
		action?: { method: string }
	} | null>(null)
	const [isSaving, setIsSaving] = useState(false)
	const [docExportInProgress, setDocExportInProgress] = useState(false)

	const gridRef = useRef<DataGrid<any, any> | null>(null)
	const editingRowDataRef = useRef<any>(null)
	const changesRef = useRef<any[]>([])
	const saveConfirmedRef = useRef<boolean | undefined>(false)
	const selectedRowRef = useRef<any>(null)
	const duplicateInitRef = useRef<Record<string, unknown> | null>(null)
	const filterLookupsRef = useRef<any>([])
	const dynamicLookupCacheRef = useRef<Record<string, { value: any[] }>>({})
	const { user } = useAuth()
	const canExportDetail = canExportView(user, folder || '', model || '')

	// Ref to store cancel token for the data request
	const dataRequestCancelToken = useRef<CancelTokenSource | null>(null)

	useBeforeUnload(editorMode)

	const fieldsDataTypes: Array<string> = []
	const { track: trackDocExport } = useDocExportProgress({ subscribe: false })

	const handleMapPopupClose = () => {
		setShowMap(false)
	}

	const handleClick = (data: any, model: string, filter: any) => {
		if (filter && filter.length > 0) {
			const clonedFilter = filter.slice()
			const value = data[0][clonedFilter[2]]
			clonedFilter[2] = value
			const base64 = btoa(clonedFilter)
			const url = `/m?folder=${folder}&model=${model}&filter=${encodeURIComponent(base64)}`
			window.open(url, '_blank')
		} else {
			const url = `/m?folder=${folder}&model=${model}`
			window.open(url, '_blank')
		}
	}

	const onFocusedRowChanged = useCallback(
		(e: any) => {
			if (key && e.row && e.row.data) {
				selectedRowRef.current = e.row.data
				if (showMap) {
					setSelectedRow(e.row.data)
				}
			}
		},
		[key, showMap]
	)

	useEffect(() => {
		const handleResize = () => {
			const viewportHeight = window.innerHeight
			setHeight(viewportHeight - 37)
		}

		handleResize()
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	const loadDynamicLookup = useCallback(
		(dataField: string) => {
			let url = `${API_FIBER_URL}/view/lookup?folder=${folder}&model=${model}&field=${dataField}`
			if (dataFilter) {
				url = `${url}&filter=${dataFilter}`
			}
			axios
				.get(url)
				.then(response => response.data)
				.then(data => {
					setDynamicLookups(dynamicLookups => ({
						...dynamicLookups,
						[dataField]: data.lookup,
					}))
				})
		},
		[folder, model, dataFilter]
	)

	useEffect(() => {
		if (model) {
			axios
				.get(`${API_FIBER_URL}/view/fields?folder=${folder}&model=${model}`)
				.then(response => response.data.data)
				.then(data => {
					setKey(data.key)
					setFields(data.fields)
					setDetail(data.detail)
					setButtons(data.buttons)
					setLookups(data.lookups)
					setLookupHeaderFilterAsText(data.lookupHeaderFilterAsText ?? [])
					setFilterLookups(data.filterLookups)
					if (data.dynamicFilterLookups) setDynamicFilterLookups(data.dynamicFilterLookups)
					setDynamicLookupServerSearch(data.dynamicLookupServerSearch || [])
					setDynamicLookupHintText(data.dynamicLookupHintText || {})
					setDynamicLookupClearDependencies(data.dynamicLookupClearDependencies || {})
					setAutoFillOnInsert(data.autoFillOnInsert || [])
					setSizes(data.sizes)
					setLocks(data.locks)
					setLocksEdit(data.locksEdit)
					setDynamicLocks(data.dynamicLocks)
					setSort(data.sort)
					setInserting(data.inserting)
					setUpdating(data.updating)
					setDeleting(data.deleting)
					setDuplicating(data.duplicating)
					setDuplicateFields(
						Array.isArray(data.duplicateFields) && data.duplicateFields.length > 0
							? data.duplicateFields
							: null
					)
					setMultiSelect(!!data.multiSelect)
					setBulkUpdating(data.bulkUpdating)
					setModelText(data.text || model)
					setHovers(data.hovers)
					setUrls(data.urls)
					setMap(data.map)
					setRowColors(data.rowColors)
					setCellColors(data.cellColors)
					setMapColorFields(data.mapColorFields)
					setPercentagesFields(data.percentagesFields)
					setBadgeFields(Array.isArray(data.badgeFields) ? data.badgeFields : [])
					setClickableFields(data.clickableFields)
					setClickablePopups(data.clickablePopups || {})
					setHintText(data.hintText || {})
					setFileUploadFields(data.fileUploadFields)
					setMultipleUploadFields(data.multipleUploadFields)
					setJsonFields(data.jsonFields)
					setAutocompleteFields(data.autocompleteFields || [])
					setExportWithPhotosOrFiles(data.exportWithPhotosOrFiles)
					setMultipleLookups(data.multipleLookups)
					setCustomLabel(data.customLabel)
					setNoDataText(data.noDataText)
					setPopupInsertVisible(data.popupInsertVisible || [])
					setEditMode(data.editMode || 'cell')
					setPopupInsertOnly(data.popupInsertOnly)
					setFieldPlaceholders(data.fieldPlaceholders || {})
					setConditionalConfirmDialog(data.conditionalConfirmDialog || [])
					setInsertConfirmDialog(data.insertConfirmDialog || null)
					if (data.exportLocked) {
						setExportLockedTime(Date.now())
						setTimeout(() => {
							setExportLockedTime(null)
						}, 60000)
					}

					Object.keys(data.dynamicLookups).forEach(k => {
						if (!(data.multipleLookups ?? []).includes(k)) {
							loadDynamicLookup(k)
						}
					})

					axios
						.post(`${API_FIBER_URL}/view/track`, {
							folder,
							model,
						})
						.catch(error => {
							console.error('Error tracking model access:', error)
						})
				})
		}
		// eslint-disable-next-line
	}, [folder, model])

	useEffect(() => {
		if (detail.model) {
			axios
				.get(`${API_FIBER_URL}/view-detail/fields?folder=${folder}&model=${detail.model}&master=${model}`)
				.then(response => response.data.data)
				.then(data => {
					setDetailFieldsSettings(data)
					setDetailFieldsSizes(data.sizes)
					setDetailFields(data.fields)
				})
		}
	}, [folder, model, detail])

	useEffect(() => {
		const keysToLoad = Object.keys(dynamicLookups).filter(k => !multipleLookups.includes(k))
		if (keysToLoad.length > 0) {
			keysToLoad.forEach(k => {
				loadDynamicLookup(k)
			})
		}
		// eslint-disable-next-line
	}, [dataFilter])

	useEffect(() => {
		if (modelText) {
			document.title = `Fiber :: ${modelText}`
		}
	}, [modelText])

	useEffect(() => {
		if (!dataFilter && defaultFilter) {
			return
		}
		if (model) {
			// Cancel previous request if it exists
			if (dataRequestCancelToken.current) {
				dataRequestCancelToken.current.cancel('Request cancelled due to filter change')
			}

			// Create new cancel token for this request
			const cancelTokenSource = axios.CancelToken.source()
			dataRequestCancelToken.current = cancelTokenSource

			const params: any = { folder, model, skip: 0, take: 1, requireTotalCount: 1 }
			if (dataFilter) {
				params.filter = dataFilter
			}
			axios
				.post(`${API_FIBER_URL}/view/data`, params, {
					cancelToken: cancelTokenSource.token,
				})
				.then(response => response.data)
				.then(data => {
					setTotalCount(data.totalCount)
				})
				.catch(error => {
					// Don't handle errors if the request was cancelled
					if (axios.isCancel(error)) {
						return
					}
					console.error('Error fetching total count:', error)
				})
		}
	}, [folder, model, dataFilter, removed, defaultFilter])

	// Keep ref in sync with state
	useEffect(() => {
		filterLookupsRef.current = filterLookups
	}, [filterLookups])

	const updateFilterLookups = useCallback((newFilterLookups: any) => {
		if (JSON.stringify(newFilterLookups) !== JSON.stringify(filterLookupsRef.current)) {
			setFilterLookups(newFilterLookups)
		}
	}, [])

	useEffect(() => {
		if (key) {
			const ds = new CustomStore({
				key: key,
				load: (loadOptions: any) => {
					if (grouping === 0) {
						const filter = isNotEmpty(loadOptions['filter']) ? JSON.stringify(loadOptions['filter']) : null
						setDataFilter(filter)
					}
					return loadData('view', loadOptions, { folder, model })
				},
				insert: values => {
					return insertData('view', { values, folder, model }).then((data: any) => {
						updateFilterLookups(data.filterLookups)
					})
				},
				update: (key, values) => {
					return editData('view', { key, values, folder, model }).then((data: any) => {
						updateFilterLookups(data.filterLookups)
					})
				},
				remove: key => {
					return deleteData('view', { key, folder, model })
				},
			})
			setDataSource(ds as any)
		}
	}, [folder, model, key, grouping])

	function filterConvertData(field: string, data: string) {
		switch (fieldsDataTypes[field as any]) {
			case 'date':
				data = data.substring(0, 10)
				break
		}
		return data
	}

	function DetailSection(props: any) {
		const data = props.data.data
		const { filter, composeFilter } = detail
		let filterClone: any = []
		if (composeFilter) {
			filterClone = JSON.parse(JSON.stringify(composeFilter))
			Object.entries(filterClone).forEach(([index, filterItem]) => {
				if (Array.isArray(filterItem)) {
					filterClone[index as any][2] = filterConvertData(filterItem[2], data[filterItem[2]])
				}
			})
		} else {
			filterClone = JSON.parse(JSON.stringify(filter))
			filterClone[2] = filterConvertData(filterClone[2], data[filterClone[2]])
		}

		return (
			<Detail
				folder={folder}
				model={detail?.model}
				master={model}
				settings={detailFieldsSettings}
				sizes={detailFieldsSizes}
				fields={detailFields}
				canExport={canExportDetail}
				refreshSettings={(action: string, data: any) => {
					switch (action) {
						case 'reorder':
						case 'hide':
							setDetailFields(data.fields)
							setDetailFieldsSizes(data.sizes)
							break
						case 'resize':
							setDetailFieldsSizes(data.sizes)
							break
					}
				}}
				filter={filterClone}
			/>
		)
	}

	function UploadSection(props: any) {
		const name = props.data.column.dataField
		const settings = fileUploadFields[name]
		const value = props.data.value
		const setValue = props.data.setValue
		return (
			<UploadField
				folder={folder}
				model={model}
				name={name}
				value={value}
				setValue={setValue}
				settings={settings}
			/>
		)
	}

	const isFieldDynamicallyLocked = (dataField: string, rowData: any, isNewRow: boolean = false): boolean => {
		if (!dynamicLocks || !dataField) {
			return false
		}
		const lockArray = Array.isArray(dynamicLocks) ? dynamicLocks : [dynamicLocks]
		for (const lock of lockArray) {
			const fields = lock.fields || []
			if (dataField === lock.field && (fields.length === 0 || !fields.includes(dataField))) {
				continue
			}
			if (fields.length > 0 && !fields.includes(dataField)) {
				continue
			}
			const rowValue = rowData?.[lock.field]
			if (lock.condition === '=' && rowValue == lock.value) {
				return true
			}
			const unsetOnNewRow = isNewRow && rowValue === undefined
			if ((lock.condition === '!=' || lock.condition === '<>') && !unsetOnNewRow && rowValue != lock.value) {
				return true
			}
		}
		return false
	}

	function MultipleSelectSection(props: any) {
		const field = props.data.column.dataField as string
		const dynamicallyLocked = isFieldDynamicallyLocked(field, props.data.data, props.data.row?.isNewRow)
		const isContextDynamicLookup =
			multipleLookups.includes(field) &&
			Object.values(dynamicLookupClearDependencies).some(deps => Array.isArray(deps) && deps.includes(field))
		const rawStatic = isContextDynamicLookup ? null : lookups[field] ?? dynamicLookups[field] ?? null
		const isDynamicLookup = isContextDynamicLookup || rawStatic === null
		const [lookupItems, setLookupItems] = useState<any[] | null>(rawStatic !== null ? lookupStore(rawStatic) : null)

		useEffect(() => {
			if (!isDynamicLookup) {
				setLookupItems(lookupStore(lookups[field] ?? dynamicLookups[field]))
				return
			}
			const raw = editingRowDataRef.current ?? props.data.data ?? {}
			const dataStr = JSON.stringify(
				Object.fromEntries(Object.entries(raw).filter(([, v]) => v != null && v !== ''))
			)
			const cacheKey = `${folder}|${model}|${field}|${dataStr}`
			if (!isContextDynamicLookup) {
				const cached = dynamicLookupCacheRef.current[cacheKey]
				if (cached?.value) {
					setLookupItems(lookupStore(cached.value))
					return
				}
			}
			const baseUrl = `${API_FIBER_URL}/view/lookup?folder=${folder}&model=${model}&field=${field}&data=${encodeURIComponent(
				dataStr
			)}`
			axios.get(baseUrl).then(res => {
				const list = res.data?.lookup ?? []
				if (!isContextDynamicLookup) {
					dynamicLookupCacheRef.current[cacheKey] = { value: list }
				}
				setLookupItems(lookupStore(list))
			})
		}, [
			field,
			isDynamicLookup,
			props.data.data,
			lookups,
			dynamicLookups,
			dynamicLookupClearDependencies,
			folder,
			model,
		])

		if (lookupItems === null) {
			return <div className="multiple-select-field">Loading...</div>
		}

		if (dynamicallyLocked || (editorMode && isDynamicLookup)) {
			return (
				<div className="dx-state-disabled">
					<MultipleSelectCellDisplay values={props.data.value} lookupList={lookupItems} />
				</div>
			)
		}
		const lookup = props.data.column?.lookup ?? {}
		return (
			<MultipleSelect
				autoOpen={!editorMode}
				data={{
					...props.data,
					lookupItems,
					column: {
						...props.data.column,
						lookup: {
							...lookup,
							dataSource: lookup.dataSource?.length ? lookup.dataSource : lookupItems,
							valueExpr: lookup.valueExpr ?? 'value',
							displayExpr: lookup.displayExpr ?? 'text',
						},
					},
				}}
			/>
		)
	}

	function renderFilterColumn(dataField: string) {
		// Check if dataField exists in fields data
		const fieldExists = fields.some((field: any) =>
			Array.isArray(dataField) ? field.dataField === dataField[0] : field.dataField === dataField
		)

		if (fieldExists) {
			return null
		}

		return (
			<Column
				key={dataField}
				dataField={dataField}
				dataType="string"
				visible={false}
				showInColumnChooser={false}
			/>
		)
	}

	const allowEditing = useCallback(
		(field: string, allowEditing: any): boolean => {
			const insertMode = isNewRow && !editorMode
			if (model && canEditField(user, model, field)) {
				return true
			}

			if (allowEditing) {
				if (insertMode) {
					return allowEditing
				}
				if (locksEdit.length === 0) {
					return allowEditing
				}
			}
			if (field === key) {
				return false
			}
			if (locks === true) {
				if (insertMode) {
					return false
				}
				if (locksEdit.length === 0) {
					return false
				}
			}
			if (Array.isArray(locks) && (locks as []).indexOf(field as never) > -1) {
				return false
			}
			const locksAr = (!insertMode || editorMode) && locksEdit.length > 0 ? locksEdit : (locks as [])
			if (locksAr.indexOf(field as never) > -1) {
				if (editorMode && locksEdit.indexOf(field as never) > -1) {
					return true
				}
				return false
			}
			if (editorMode && dynamicLookups[field]) {
				return false
			}
			return true
		},
		[locks, locksEdit, editorMode, dynamicLookups, key, user, model, isNewRow]
	)

	let rowParsed = false

	const rowPrepared = useCallback(
		(e: any) => {
			if (e.rowType === 'data') {
				if (e.dataIndex === 0 && !e.selected) {
					if (!rowParsed) {
						e.rowElement.classList.add('dx-row-focused', 'dx-selection')
						rowParsed = true
					}
				}
				const { data: rowData, rowElement } = e
				rowColors.forEach(({ field, fieldValue, field2, fieldValue2, operator, color }) => {
					let shouldApplyColor = false
					const matchesCondition = (fieldName: string, expectedValue: any) => {
						if (expectedValue === 'not null') {
							return (
								rowData[fieldName] !== null &&
								rowData[fieldName] !== undefined &&
								rowData[fieldName] !== ''
							)
						}
						return rowData[fieldName] === expectedValue
					}

					switch (operator) {
						case 'AND':
							shouldApplyColor =
								matchesCondition(field, fieldValue) && matchesCondition(field2, fieldValue2)
							break
						case 'OR':
							shouldApplyColor =
								matchesCondition(field, fieldValue) || matchesCondition(field2, fieldValue2)
							break
						default:
							shouldApplyColor = matchesCondition(field, fieldValue)
					}
					if (shouldApplyColor) {
						rowElement.style.backgroundColor = color
					}
				})
			}
		},
		[rowColors]
	)

	const cellPrepared = (e: any) => {
		if (e.rowType === 'data') {
			const rowData = e.data
			if (jsonFields.indexOf(e.column.dataField as never) > -1) {
				const editorWithMenu = e.cellElement.querySelector('.dx-editor-with-menu')
				if (editorWithMenu) {
					editorWithMenu.remove()
				}
			}
			cellColors.forEach((item: any) => {
				if (e.column.dataField !== item.field) {
					return
				}
				if (item.compareTo) {
					const rawA = rowData[item.field]
					const rawB = rowData[item.compareTo]
					if (rawA == null || rawA === '' || rawB == null || rawB === '') {
						return
					}
					const a = Number(rawA)
					const b = Number(rawB)
					if (Number.isNaN(a) || Number.isNaN(b)) {
						return
					}
					if (
						(item.when === 'gt' && a > b) ||
						(item.when === 'lt' && a < b) ||
						(item.when === 'eq' && a === b) ||
						(item.when === 'neq' && a !== b)
					) {
						e.cellElement.style.cssText = `background-color: ${item.color}`
					}
					return
				}
				if (rowData[item.field] && rowData[item.field] === item.fieldValue) {
					e.cellElement.style.cssText = `background-color: ${item.color}`
				}
			})
		} else if (e.rowType === 'header') {
			const tooltipText = hovers[e.column.dataField] || ''
			e.cellElement.setAttribute('title', tooltipText)

			// Hide dx-editor-with-menu for multi-upload columns
			if (multipleUploadFieldsAr.indexOf(e.column.dataField) > -1) {
				const editorWithMenu = e.cellElement.querySelector('.dx-editor-with-menu')
				if (editorWithMenu) {
					editorWithMenu.remove()
				}
			}
		} else if (e.rowType === 'filter') {
			// Hide dx-editor-with-menu for multi-upload columns in filter row
			if (multipleUploadFieldsAr.indexOf(e.column.dataField) > -1) {
				const editorWithMenu = e.cellElement.querySelector('.dx-editor-with-menu')
				if (editorWithMenu) {
					editorWithMenu.remove()
				}
			}
		}
	}

	const onEditingStart = (e: any) => {
		editingRowDataRef.current = e.data
		if (!e.row?.isNewRow && e.column?.dataField && isFieldDynamicallyLocked(e.column.dataField, e.data)) {
			e.cancel = true
			return
		}
		if (e.row && typeof e.row.isNewRow === 'boolean') {
			syncNewRow(e.row.isNewRow)
		}
	}

	const isDynamicallyLocked = (e: any): boolean =>
		e.parentType === 'dataRow' && isFieldDynamicallyLocked(e.dataField, e.row?.data, e.row?.isNewRow)

	const onEditorPreparing = (e: any) => {
		if (e.parentType === 'filterRow' && e.dataField && lookupHeaderFilterAsText.includes(e.dataField)) {
			e.editorName = 'dxTextBox'
			delete e.lookup
			const eo = e.editorOptions ?? (e.editorOptions = {})
			delete eo.dataSource
			delete eo.items
			delete eo.valueExpr
			delete eo.displayExpr
			delete eo.searchEnabled
			delete eo.searchMode
			delete eo.searchTimeout
			delete eo.minSearchLength
			delete eo.showDataBeforeSearch
			delete eo.acceptCustomValue
			eo.mode = 'text'
			eo.showClearButton = true
		}
		if (e.parentType === 'dataRow' && e.dataField) {
			if (editorMode && locksEdit.length > 0 && locksEdit.indexOf(e.dataField as never) > -1) {
				e.editorOptions.readOnly = !e.row?.isNewRow
			}
			const prev = e.editorOptions.onValueChanged
			e.editorOptions.onValueChanged = (args: any) => {
				if (autoFillInProgressRef.current && isNewRowRef.current && editMode === 'popup') {
					return
				}
				const selectedItem = args?.component?.option?.('selectedItem')
				const rawValue = args.value
				const normalizedValue = selectedItem?.actual_value != null ? selectedItem.actual_value : rawValue
				if (normalizedValue !== rawValue) {
					e.setValue?.(normalizedValue)
				}
				prev?.({ ...args, value: normalizedValue })
				const nextRowData = {
					...(editingRowDataRef.current ?? e.row?.data ?? {}),
					[e.dataField]: normalizedValue,
				}
				const dependentFields = dynamicLookupClearDependencies[e.dataField]
				if (dependentFields?.length) {
					for (const dep of dependentFields) {
						nextRowData[dep] = null
					}
					const rowIndex = e.row?.rowIndex
					if (rowIndex != null && gridRef.current?.instance) {
						for (const dep of dependentFields) {
							gridRef.current.instance.cellValue(rowIndex, dep, null)
						}
					}
				}

				const hintTextField = dynamicLookupHintText[e.dataField]
				if (hintTextField && dependentFields?.length && selectedItem) {
					const normalizedHintField = hintTextField.toLowerCase().replace(/[_\s]+/g, '')
					const targetField = dependentFields.find(
						(dep: string) => dep.toLowerCase().replace(/[_\s]+/g, '') === normalizedHintField
					)
					const hintValue = selectedItem?.[hintTextField]
					if (targetField && hintValue != null && String(hintValue).trim() !== '') {
						nextRowData[targetField] = hintValue
						const rowIndex = e.row?.rowIndex
						if (rowIndex != null && gridRef.current?.instance) {
							gridRef.current.instance.cellValue(rowIndex, targetField, hintValue)
						}
					}
				}
				editingRowDataRef.current = nextRowData

				if (dependentFields?.length) {
					const rowIndex = e.row?.rowIndex
					void runAutoFillCascade(nextRowData, dependentFields, (field, value) => {
						if (rowIndex != null && gridRef.current?.instance) {
							gridRef.current.instance.cellValue(rowIndex, field, value)
						}
					}).then(resolvedRowData => {
						editingRowDataRef.current = resolvedRowData
					})
				}
			}
		}

		if (e.parentType === 'dataRow' && e.dataField && editMode === 'popup') {
			const isSelectEditor = e.editorName === 'dxSelectBox' || e.editorName === 'dxLookup'
			e.editorOptions.placeholder = fieldPlaceholders[e.dataField] ?? (isSelectEditor ? 'Select...' : undefined)
		}

		if (autocompleteFields.includes(e.dataField)) {
			const isInt = e.column?.dataType === 'int'
			const buildItem = (raw: string | undefined) => {
				const text = typeof raw === 'string' ? raw.trim() : ''
				if (!text) return null
				const value = isInt && !Number.isNaN(Number(text)) ? Number(text) : text
				return { value, text }
			}
			e.editorOptions.onCustomItemCreating = (args: any) => {
				args.customItem = buildItem(args.text)
			}
			e.editorOptions.onEnterKey = (args: any) => {
				const item = buildItem(args.component?.option('text'))
				if (item) {
					e.setValue?.(item.value)
					args.component?.option('value', item.value)
				}
				gridRef.current?.instance?.closeEditCell()
			}
		}

		if (
			e.parentType === 'dataRow' &&
			e.dataField &&
			lookupHeaderFilterAsText.includes(e.dataField) &&
			lookups[e.dataField] &&
			!dynamicLookups[e.dataField]
		) {
			e.editorName = 'dxSelectBox'
			e.editorOptions.valueExpr = 'value'
			e.editorOptions.displayExpr = 'text'
			e.editorOptions.dataSource = lookupStore(lookups[e.dataField])
			e.editorOptions.searchEnabled = true
			e.editorOptions.searchMode = 'contains'
		}

		if (e.parentType === 'dataRow' && dynamicLookups[e.dataField]) {
			e.editorName = 'dxSelectBox'
			const serverSearch = dynamicLookupServerSearch.includes(e.dataField)
			e.editorOptions.valueExpr = 'value'
			e.editorOptions.displayExpr = 'text'
			e.editorOptions.inputAttr = { autocomplete: 'off', name: `no-autofill-${e.dataField}` }
			e.editorOptions.dataSource = new DataSource({
				store: new CustomStore({
					key: 'value',
					load: async (opts: any) => {
						const raw = editingRowDataRef.current ?? {}
						const dataStr = JSON.stringify(
							isNewRowRef.current
								? Object.fromEntries(Object.entries(raw).filter(([, v]) => v != null && v !== ''))
								: raw
						)
						const typedSearch = opts?.searchValue != null ? String(opts.searchValue).trim() : ''
						const currentValue =
							raw[e.dataField] != null && String(raw[e.dataField]).trim() !== ''
								? String(raw[e.dataField]).trim()
								: ''
						const searchParam = typedSearch !== '' ? typedSearch : currentValue
						const baseUrl = `${API_FIBER_URL}/view/lookup?folder=${folder}&model=${model}&field=${
							e.dataField
						}&data=${encodeURIComponent(dataStr)}`

						if (serverSearch) {
							const cache = dynamicLookupCacheRef.current
							const cacheKey = `${folder}|${model}|${e.dataField}|${dataStr}|${searchParam}`
							if (cache[cacheKey]) {
								const list = cache[cacheKey].value
								const skip = opts?.skip ?? 0
								const take = opts?.take ?? list.length
								const data = list.slice(skip, skip + take)
								return opts?.requireTotalCount ? { data, totalCount: list.length } : data
							}
							const res = await axios.get(
								baseUrl + (searchParam ? `&search=${encodeURIComponent(searchParam)}` : '')
							)
							const list = res.data?.lookup ?? []
							cache[cacheKey] = { value: list }
							const skip = opts?.skip ?? 0
							const take = opts?.take ?? list.length
							const data = list.slice(skip, skip + take)
							return opts?.requireTotalCount ? { data, totalCount: list.length } : data
						}

						const cache = dynamicLookupCacheRef.current
						const key = `${folder}|${model}|${e.dataField}|${dataStr}`
						if (!cache[key]) cache[key] = { value: [] }
						const cached = cache[key]
						if (!cached.value.length) {
							const res = await axios.get(baseUrl)
							cached.value = res.data?.lookup ?? []
						}
						const searchRaw = opts?.searchValue != null ? String(opts.searchValue).trim() : currentValue
						const search = searchRaw.toLowerCase()
						const list =
							search === ''
								? cached.value
								: cached.value.filter((item: any) =>
										(item?.text ?? item?.value ?? '').toString().toLowerCase().includes(search)
								  )
						const skip = opts?.skip ?? 0
						const take = opts?.take ?? list.length
						const data = list.slice(skip, skip + take)
						return opts?.requireTotalCount ? { data, totalCount: list.length } : data
					},
					byKey: (k: any) => Promise.resolve({ value: k, text: k }),
				}),
			})
			e.editorOptions.searchEnabled = true
			e.editorOptions.searchMode = 'contains'
			e.editorOptions.searchTimeout = 500
			e.editorOptions.minSearchLength = 2
			e.editorOptions.showDataBeforeSearch = !serverSearch
			e.editorOptions.acceptCustomValue = true
			e.editorOptions.dropDownOptions = {
				width: 'auto',
				minWidth: 300,
				maxWidth: '90vw',
				resizeEnabled: true,
			}
			const hintTextField = dynamicLookupHintText[e.dataField]
			if (hintTextField) {
				e.editorOptions.itemTemplate = (itemData: any) => {
					const main = String(itemData?.text ?? '')
					const hint = itemData?.[hintTextField] ? String(itemData[hintTextField]) : ''
					if (!hint) return main
					const el = document.createElement('div')
					el.innerHTML = `<div>${main}</div><div style="font-size:11px;color:#888;margin-top:2px">${hint}</div>`
					return el
				}
			}
			e.editorOptions.onKeyDown = (args: any) => {
				if ((args.event?.ctrlKey || args.event?.metaKey) && args.event?.key?.toLowerCase() === 'v') {
					args.event.preventDefault()
				}
			}
		}

		if (isDynamicallyLocked(e)) {
			e.editorOptions.disabled = true
		}
	}

	const rightClickFilterOptions = (e: any) => {
		const dataField = e.column?.dataField

		let value = e.row?.data ? e.row?.data[dataField] : undefined
		if (value && e.column.dataType === 'date') {
			value = value.substring(0, 10)
		}
		let displayValue = minifiyUrl(value)

		if (e.target === 'content') {
			e.items = e.items || []
		}

		const addFilterItem = (text: any, selectedFilterOperation: any) => {
			const options = {
				filterValue: value,
				selectedFilterOperation,
			}
			e.items.push({
				text: `${text} "${displayValue}"`,
				icon: 'filter',
				onItemClick: () => {
					if (e.column.dataType === 'date') {
						options.filterValue = new Date(value)
					}
					gridRef.current?.instance.columnOption(dataField, options)
				},
			})
		}

		const addBulkEditItem = (dataField: string, bulkValue: any, bulkDisplayValue: string) => {
			e.items.push({
				text: `Bulk Edit: Column: "${dataField}" with value = "${bulkDisplayValue}".`,
				icon: 'tableproperties',
				onItemClick: async () => {
					const clickedAction = await confirmDialog(
						'Are you sure you want to perform bulk edit?',
						`You are going to edit ${totalCount} rows where column is "${dataField}" and the value will be: "${bulkDisplayValue}".`
					)
					if (clickedAction) {
						let url = `${API_FIBER_URL}/view/multiUpdate?`
						url = `${url}&model=${model}&folder=${folder}&field=${encodeURIComponent(
							dataField
						)}&value=${encodeURIComponent(bulkValue)}`
						if (dataFilter) {
							url = `${url}&filter=${encodeURIComponent(dataFilter)}`
						}
						try {
							axios
								.put(url)
								.then(response => response.data)
								.then((data: any) => {
									notify(
										`Bulk edit successful: ${totalCount} rows updated with "${bulkDisplayValue}" in column "${dataField}"`,
										'success'
									)
									updateFilterLookups(data.filterLookups)
									gridRef.current?.instance?.refresh()
								})
						} catch (error: any) {
							if (error.response && error.response.status === 400) {
								notify(error.response.data.message, 'error')
							} else {
								notify(`Bulk edit failed`, 'error')
							}
						}
					}
				},
			})
		}

		if (e.target === 'header' && mapColorFields && (mapColorFields as string[]).includes(dataField)) {
			e.items.push({
				text: 'Color by Column Values',
				icon: 'palette',
				onItemClick: async () => {
					try {
						const url = `${API_FIBER_URL}/view/distinct/?model=${model}&folder=${folder}&field=${dataField}`
						const response = await axios.get(url)
						Object.keys(response.data.distinctWithColors).forEach(k => {
							const newData = response.data.distinctWithColors[k]
							setValueColors((prev: any) => ({
								...prev,
								[k]: newData,
							}))
						})
					} catch (error) {
						console.error('Error retrieving distinct values:', error)
					}
				},
			})
		}

		const canBulkUpdate =
			bulkUpdating &&
			totalCount &&
			totalCount <= 100 &&
			dataFilter &&
			locks &&
			!(locks as string[]).includes(dataField)

		if (
			e.target === 'content' &&
			(e.targetElement.className === 'dx-checkbox-icon' || e.targetElement.querySelector('.dx-checkbox-icon')) &&
			dataField &&
			canBulkUpdate
		) {
			addBulkEditItem(dataField, true, 'true')
			addBulkEditItem(dataField, false, 'false')
		} else if (e.target === 'content' && e.targetElement?.closest?.('td') && dataField) {
			if (lookups.hasOwnProperty(dataField)) {
				const lookup = filterLookups[dataField] ?? lookups[dataField]
				if (lookup[value]) {
					displayValue = lookup[value]
				}
				if (Array.isArray(lookup) && !(lookup as string[]).includes(value)) {
					value = displayValue = null
				}
			}

			const headerFilter = e.column.headerFilter?.dataSource
			if (value !== undefined) {
				if (headerFilter) {
					addFilterItem('Equal', '')
				} else if (value) {
					if (e.column.dataType === 'date') {
						addFilterItem('Equal', '=')
					} else {
						addFilterItem('Contains', null)
						addFilterItem('Does Not Contains', 'notcontains')
						addFilterItem('Equal', '=')
						addFilterItem('Does Not Equal', '<>')
					}
				}
				if (displayValue) {
					e.items.push({
						text: `Copy "${displayValue}"`,
						icon: 'copy',
						onItemClick: () => {
							const elem = document.createElement('textarea')
							elem.value = value
							document.body.appendChild(elem)
							elem.select()
							document.execCommand('copy')
							document.body.removeChild(elem)
							notify(`${displayValue} copied to clipboard`, 'info')
							setClipboard({ value, displayValue, dataType: e.column.dataType })
						},
					})
				}
				if (canBulkUpdate && displayValue) {
					let bulkValue = value
					let bulkDisplayValue = displayValue
					if (clipboard && e.column.dataType === clipboard.dataType) {
						bulkValue = clipboard.value
						bulkDisplayValue = clipboard.displayValue

						if (bulkDisplayValue !== displayValue) {
							e.items.push({
								text: `Clear clipboard`,
								icon: 'clear',
								onItemClick: () => {
									notify(`The clipboard was cleared`, 'info')
									setClipboard(null)
								},
							})
						}
					}
					addBulkEditItem(dataField, bulkValue, bulkDisplayValue)
				}
			}

			e.items.push({
				text: `Audit Trail`,
				icon: 'floppy',
				onItemClick: () => {
					setAuditData(e.row.data)
					setAuditField(dataField)
					setShowAuditTrail(true)
				},
			})
		}
	}

	const handleDuplicate = useModelDuplicateGrid({
		gridRef,
		selectedRowRef,
		editorMode,
		duplicateFields,
		key,
		duplicateInitRef,
	})

	const isRequiredValueMissing = (value: any) =>
		value === null || value === undefined || (typeof value === 'string' && value.trim() === '')

	const getMissingRequiredFieldLabels = (rowData: any) =>
		fields
			.filter((field: any) => field.required)
			.filter((field: any) => isRequiredValueMissing(rowData[field.dataField]))
			.map((field: any) => field.caption ?? field.dataField)

	const getRowDataForChange = (change: any) => {
		if (change.type === 'insert') {
			return change.data ?? {}
		}

		const grid = gridRef.current?.instance
		if (!grid) {
			return change.data ?? {}
		}

		const rowIndex = grid.getRowIndexByKey(change.key)
		if (rowIndex >= 0) {
			return { ...grid.getVisibleRows()[rowIndex]?.data }
		}

		const row = grid.getVisibleRows().find((r: any) => {
			if (!r.data) return false
			return JSON.stringify(grid.keyOf(r.data)) === JSON.stringify(change.key)
		})
		if (row?.data) {
			return { ...row.data }
		}

		return change.data ?? {}
	}

	const notifyMissingRequiredFields = (labels: string[]) => {
		if (labels.length) {
			notify(`Required Fields: ${labels.join(', ')}`, 'error')
		}
	}

	const handleSaveData = async () => {
		const changes = changesRef.current
		const missingLabels = new Set<string>()
		for (const change of changes) {
			getMissingRequiredFieldLabels(getRowDataForChange(change)).forEach(label => missingLabels.add(label))
		}
		if (missingLabels.size) {
			notifyMissingRequiredFields(Array.from(missingLabels))
			return
		}
		const clickedAction = await confirmDialog('Multiple edit', `You are going to edit ${changes.length} rows.`)
		if (clickedAction) {
			setIsSaving(true)
			const url = `${API_FIBER_URL}/view/massUpdate`
			const requestData = {
				model: model,
				folder: folder,
				changes: changes,
			}

			axios
				.patch(url, requestData)
				.then(response => response.data)
				.then((data: any) => {
					notify(`Data updated successfully`, 'success')
					updateFilterLookups(data.filterLookups)
					setTimeout(() => {
						gridRef.current?.instance?.refresh()
					}, 50)
					setEditorMode(false)
					changesRef.current = []
				})
				.catch(() => {
					notify(`Multiple edit error!`, 'error')
				})
				.finally(() => {
					setIsSaving(false)
				})
		}
	}

	const withHintText = (dataField: string, render?: (cellInfo: any) => React.ReactNode) =>
		cellWithHintText(hintText, dataField, render)

	const clickToField = (field: string, data: any) => {
		setShowClickableField({ field, data })
	}

	const breadcrumb = (folders: any) => {
		let path = '' // Initialize an empty path
		return (
			folders &&
			folders.split('/').map((folder: string) => {
				path += `/${folder}` // Append current folder and a slash to the path
				return (
					<React.Fragment key={path}>
						<Link className="link" to={`/v${path}`}>
							{folder}
						</Link>{' '}
						/{' '}
					</React.Fragment>
				)
			})
		)
	}

	const headerDynamicLookups = (dataField: string) => {
		const list = dynamicFilterLookups[dataField] ?? dynamicLookups[dataField]
		if (!list || !Array.isArray(list)) return []
		return list.map((item: any) => {
			const text = item?.text ?? item?.value ?? item
			return { value: text, text }
		})
	}

	const renderButtonPopContent = () => {
		const popupArgs: any = buttonArgs as any
		if (popupArgs && popupArgs.popupType === 'DataGridPopup') {
			return <DataGridPopup args={popupArgs} filter={dataFilter} />
		}
		if (popupArgs && popupArgs.popupType === 'ModelPopup') {
			return <ModelPopup args={popupArgs} />
		}
		if (popupArgs && popupArgs.popupType === 'BulkActionPopup') {
			return <BulkActionPopup args={popupArgs} filter={dataFilter} />
		}
		if (popupArgs && popupArgs.popupType === 'ModelGridPopup') {
			return <ModelGridPopup args={popupArgs} />
		}

		return <LinkPermit args={buttonArgs} filter={dataFilter} totalCount={totalCount} />
	}

	const fileUploadFieldsAr = Object.keys(fileUploadFields)
	const multipleUploadFieldsAr = Object.keys(multipleUploadFields)

	const parseDatesInFilter = (value: any): any => {
		if (Array.isArray(value)) return value.map(parseDatesInFilter)
		if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) return new Date(value)
		return value
	}

	const handleApplyFilter = (filter: any) => {
		gridRef.current?.instance.option('filterValue', parseDatesInFilter(filter))
		gridRef.current?.instance.refresh()
		notify(`Filter applied successfully!`, 'success')
	}

	const openFilterPopup = () => {
		setFilterPopupVisible(true)
	}

	const { hotkeyHelpModal } = useModelHotkeys({
		filterPopupVisible,
		buttonPopupShow,
		showAuditTrail,
		showRow,
		showMap,
	})

	const toggleButton = (button: ButtonConfig) => {
		setButtons(prevButtons =>
			prevButtons.map(btn => {
				if (btn.id === button.toggle) {
					return { ...btn, visible: true }
				}
				if (btn.id === button.id) {
					return { ...btn, visible: false }
				}
				return btn
			})
		)
	}

	const allowAdding =
		editorMode && Object.keys(dynamicLookups).length > 0
			? false
			: canInsertingView(user, folder || '', model || '', inserting)
	const allowDeleting = editorMode ? false : canDeleteView(user, folder || '', model || '', deleting)

	return (
		<React.Fragment>
			{_defaultFilter && (
				<style>
					{`
						.dx-widget.dx-checkbox.dx-checkbox-checked.dx-datagrid-filter-panel-checkbox {
							display: none !important;
						}
					`}
				</style>
			)}
			<p className={'content-block'}>
				{(customLabel || totalCount !== null) && (
					<span style={{ float: 'right' }}>
						{customLabel && (
							<span style={{ marginRight: 16, fontSize: '0.9em', color: '#666' }}>{customLabel}</span>
						)}
						{totalCount !== null && (
							<span>
								{totalCount} record{totalCount === 1 ? '' : 's'}
							</span>
						)}
					</span>
				)}
				<span className="breadcrumb-text">
					{breadcrumb(folder)} {modelText}
				</span>
			</p>

			{dataSource && (
				<div style={{ position: 'relative' }}>
					<DataGrid
						ref={gridRef}
						height={height}
						className={'dx-card wide-card'}
						dataSource={dataSource}
						noDataText={noDataText}
						dateSerializationFormat="yyyy-MM-dd"
						allowColumnReordering={true}
						remoteOperations={true}
						focusedRowEnabled={true}
						autoNavigateToFocusedRow={false}
						defaultFocusedRowIndex={0}
						hoverStateEnabled={true}
						columnAutoWidth={true}
						showBorders={false}
						allowColumnResizing={true}
						filterSyncEnabled={true}
						columnResizingMode="widget"
						onContextMenuPreparing={rightClickFilterOptions}
						onOptionChanged={(e: any) => {
							if (e.fullName === 'filterValue' && e.value === null) {
								gridRef.current?.instance.filter(defaultFilter)
							}
							if (e.fullName === 'editing.changes') {
								changesRef.current = e.value
							}
							if (e.fullName.indexOf('groupIndex') > -1) {
								if (typeof e.value !== 'undefined') {
									setGrouping(grouping + 1)
								} else {
									setGrouping(grouping - 1)
								}
							} else {
								handleOptionChange('view', e, { folder, model }, null)
							}
						}}
						defaultFilterValue={defaultFilter ?? undefined}
						onExporting={(e: any) => {
							if (exportLockedTime !== null) {
								const diff = Date.now() - exportLockedTime
								const seconds = 60 - Math.floor((diff % 60000) / 1000)
								notify(`You can export again in ${seconds} seconds.`, 'info')
								return
							}

							if (exportWithPhotosOrFiles && totalCount && totalCount > 100) {
								notify('You need to filter max 100 rows', 'info')
								return
							}

							handleExporting('view', e, { folder, model })
							setExportLockedTime(Date.now())
							setTimeout(() => {
								setExportLockedTime(null)
							}, 60000)
						}}
						onFocusedRowChanged={onFocusedRowChanged}
						onRowPrepared={rowPrepared}
						onCellPrepared={cellPrepared}
						onEditingStart={onEditingStart}
						onEditorPreparing={onEditorPreparing}
						onRowValidating={(e: any) => {
							if (e.isValid) return
							const missingLabels = (e.brokenRules ?? [])
								.map(
									(rule: any) =>
										rule.column?.caption ||
										rule.column?.dataField ||
										rule.message?.replace(/ is required$/i, '')
								)
								.filter(Boolean)
							notifyMissingRequiredFields(missingLabels)
						}}
						onInitNewRow={(e: any) => {
							syncNewRow(true)
							if (duplicateInitRef.current && e?.data) {
								Object.assign(e.data, duplicateInitRef.current)
								duplicateInitRef.current = null
							}
							editingRowDataRef.current = { ...(e?.data ?? {}) }
							const g = gridRef.current?.instance
							if (g) g.getScrollable?.()?.scrollTo?.(0)

							if (autoFillOnInsert.length) {
								e.promise = loadDynamicLookupBatch(autoFillOnInsert, editingRowDataRef.current).then(
									lookupsByField => {
										for (const field of autoFillOnInsert) {
											const items = lookupsByField?.[field] ?? []
											if (items.length !== 1) continue
											const value = items[0]?.value ?? items[0]?.text
											if (value == null || String(value).trim() === '') continue
											e.data[field] = value
											editingRowDataRef.current = { ...editingRowDataRef.current, [field]: value }
										}
									}
								)
							}
						}}
						onEditCanceled={() => {
							setPopupTitle(null)
							syncNewRow(false)
							if (popupInsertOnly) {
								setEditMode('cell')
							}
						}}
						onSaving={(e: any) => {
							if (editorMode || editMode !== 'cell') return
							const hasInsert = e.changes?.some((c: any) => c.type === 'insert')
							if (insertConfirmDialog && hasInsert) {
								if (saveConfirmedRef.current === true) {
									saveConfirmedRef.current = false
									return
								}
								if (saveConfirmedRef.current === undefined) {
									e.cancel = true
									return
								}
								e.cancel = true
								saveConfirmedRef.current = undefined
								const row = e.changes.find((c: any) => c.type === 'insert')?.data ?? {}
								const apiOpts = {
									apiBaseUrl: API_FIBER_URL,
									folder: folder || '',
									model: model || '',
									onError: (msg: string) => notify(msg, 'error'),
								}
								if (insertConfirmDialog.action) {
									showInsertWithPrecheck(
										insertConfirmDialog.title,
										insertConfirmDialog.message,
										row,
										apiOpts
									).then(result => {
										if (!result) {
											saveConfirmedRef.current = false
											gridRef.current?.instance?.cancelEditData()
										} else if (result === 'done') {
											saveConfirmedRef.current = false
											gridRef.current?.instance?.cancelEditData()
											gridRef.current?.instance?.refresh()
										} else {
											saveConfirmedRef.current = true
											gridRef.current?.instance?.saveEditData()
										}
									})
								} else {
									confirmDialog(insertConfirmDialog.title, insertConfirmDialog.message).then(
										(ok: boolean) => {
											saveConfirmedRef.current = ok
											gridRef.current?.instance[ok ? 'saveEditData' : 'cancelEditData']()
										}
									)
								}
								return
							}
							if (!conditionalConfirmDialog?.length) return
							if (saveConfirmedRef.current === true) {
								saveConfirmedRef.current = false
								return
							}
							if (saveConfirmedRef.current === undefined) {
								e.cancel = true
								return
							}
							const confirm = getConditionalConfirm(e.changes, key, conditionalConfirmDialog)
							if (!confirm) return
							e.cancel = true
							saveConfirmedRef.current = undefined
							showConditionalConfirmWithApi(confirm.title, confirm.message, confirm.row, {
								apiBaseUrl: API_FIBER_URL,
								folder: folder || '',
								model: model || '',
								onError: (msg: string) => notify(msg, 'error'),
							}).then(ok => {
								saveConfirmedRef.current = ok
								if (ok) {
									gridRef.current?.instance
										?.saveEditData()
										?.then(() => gridRef.current?.instance?.refresh())
								} else {
									gridRef.current?.instance?.cancelEditData()
								}
							})
						}}
						onRowInserted={() => {
							setPopupTitle(null)
							syncNewRow(false)
							if (popupInsertOnly) {
								setEditMode('cell')
							}
						}}
						onRowRemoved={() => {
							syncNewRow(false)
							setRemoved(removed + 1)
						}}
						onDataErrorOccurred={(e: any) => {
							if (e.error && e.error.message) {
								notify(e.error.message, 'error')
								e.handled = true
							}
						}}
					>
						{editorMode && (
							<KeyboardNavigation
								enabled={true}
								editOnKeyPress={true}
								enterKeyAction={'startEdit'}
								enterKeyDirection={'column'}
							/>
						)}
						<Scrolling mode="virtual" useNative={true} />
						<Grouping autoExpandAll={false} />
						<GroupPanel visible={true} />
						<Editing
							startEditAction={editorMode ? 'click' : 'dblClick'}
							newRowPosition="first"
							mode={editorMode ? 'batch' : editMode || 'cell'}
							allowAdding={allowAdding}
							allowUpdating={updating}
							confirmDelete={true}
							allowDeleting={allowDeleting}
						>
							<EditingPopup
								showTitle={true}
								title={popupTitle ?? (modelText ? `Edit ${modelText}` : 'Edit Record')}
								width={900}
								height={Math.max(height - 120, 420)}
							/>
							<Form colCount={2} height="100%" scrollingEnabled={false}>
								{popupInsertVisible.map((dataField: string) => {
									const field = fields.find((f: any) => f.dataField === dataField)
									const isMultipleLookupField = multipleLookups.includes(dataField)
									return (
										<FormItem
											key={dataField}
											dataField={dataField}
											colSpan={isMultipleLookupField ? 2 : undefined}
											editorOptions={{
												disabled: !allowEditing(dataField, field?.allowEditing),
											}}
										/>
									)
								})}
							</Form>
						</Editing>
						<Paging defaultPageSize={100} />
						<HeaderFilter visible={true} />
						<FilterRow visible={true} applyFilter={'onClick'} />
						<Selection
							mode={multiSelect ? 'multiple' : 'single'}
							selectAllMode={'page'}
							showCheckBoxesMode={multiSelect ? 'always' : 'none'}
						/>
						<ColumnChooser enabled />
						<FilterPanel visible={true} />
						{_defaultFilter && renderFilterColumn(_defaultFilter[0])}
						{fields.map((field: any, ndx: number) => {
							const dataType = fixDataType(field.dataType, field.dataField)
							fieldsDataTypes[field.dataField] = dataType

							const isFileUploadField = fileUploadFieldsAr.indexOf(field.dataField) > -1
							const isMultipleUploadField = multipleUploadFieldsAr.indexOf(field.dataField) > -1
							const isJsonField = jsonFields.indexOf(field.dataField as never) > -1
							const isUrlField = urls.indexOf(field.dataField as never) > -1

							const headerFilterAsTextCol = lookupHeaderFilterAsText.includes(field.dataField)
							const isMultipleLookupField = multipleLookups.indexOf(field.dataField) > -1

							const isLookupStyleFilterColumn =
								!!lookups[field.dataField] ||
								!!dynamicLookups[field.dataField] ||
								!!dynamicFilterLookups[field.dataField] ||
								isMultipleUploadField

							const allowHeaderFiltering = headerFilterAsTextCol ? false : isLookupStyleFilterColumn
							const allowFiltering = !isLookupStyleFilterColumn || headerFilterAsTextCol

							return (
								<Column
									key={field.dataField}
									dataField={field.dataField}
									dataType={dataType}
									defaultSortIndex={sort && field.dataField === sort.field ? 0 : undefined}
									defaultSortOrder={sort && field.dataField === sort.field ? sort.order : undefined}
									{...(dataType === 'boolean'
										? {
												calculateCellValue: (rowData: any) => {
													if (rowData[field.dataField] === true) {
														return true
													}
													return parseInt(rowData[field.dataField]) ? true : false
												},
										  }
										: isMultipleLookupField
										? {
												editCellComponent: MultipleSelectSection,
												filterOperations: ['isblank', 'isnotblank', 'anyof', 'noneof'],
												defaultFilterOperation: 'anyof',
												formItem: {
													colSpan: 2,
												},
										  }
										: isFileUploadField
										? {
												editCellComponent: UploadSection,
										  }
										: isMultipleUploadField
										? {
												filterOperations: ['isblank', 'isnotblank', 'anyof', 'noneof'],
												allowSorting: false,
										  }
										: headerFilterAsTextCol && dataType === 'string'
										? {
												defaultFilterOperation: 'contains',
												filterOperations: [
													'contains',
													'notcontains',
													'startswith',
													'endswith',
													'=',
													'<>',
												],
										  }
										: {})}
									format={
										field.dataType === 'custom_datetime'
											? 'dd/MM/yyyy HH:mm'
											: dataType === 'date'
											? 'dd/MM/yyyy'
											: ''
									}
									caption={field.caption ?? field.dataField}
									visible={field.visible && !isHiddenField(user, model || '', field.dataField)}
									showEditorAlways={editorMode || dataType === 'boolean'}
									allowEditing={
										!isMultipleUploadField &&
										!isJsonField &&
										allowEditing(field.dataField, field.allowEditing)
									}
									allowGrouping={field.allowGrouping ?? true}
									allowFiltering={allowFiltering}
									allowHeaderFiltering={allowHeaderFiltering}
									calculateFilterExpression={
										((filterValue: any, selectedFilterOperation: string | null, target: string) => {
											if (isMultipleLookupField) {
												if (selectedFilterOperation === 'noneof') {
													const values = Array.isArray(filterValue)
														? filterValue
														: [filterValue]
													return [field.dataField, 'noneof', values]
												}
												if (
													selectedFilterOperation === 'anyof' ||
													(Array.isArray(filterValue) && filterValue.length > 1)
												) {
													const values = Array.isArray(filterValue)
														? filterValue
														: [filterValue]
													return [field.dataField, 'anyof', values]
												}
												if (filterValue === null || filterValue === '') {
													return [field.dataField, '=', null]
												}
												return [field.dataField, selectedFilterOperation || '=', filterValue]
											}
											if (dataType !== 'date') {
												switch (selectedFilterOperation) {
													case '=':
														if (filterValue === ' ') {
															return [
																[field.dataField, '=', ''],
																'or',
																[field.dataField, '=', ' '],
																'or',
																[field.dataField, '=', null],
															]
														}
														break
													case '<>':
														if (filterValue === ' ') {
															return [
																[field.dataField, '<>', ''],
																'and',
																[field.dataField, '<>', ' '],
																'and',
																[field.dataField, '<>', null],
															]
														}
														if (filterValue !== null) {
															return [
																[field.dataField, '<>', filterValue],
																'or',
																[field.dataField, '=', null],
															]
														}
														break
													case 'notcontains':
														return [
															[field.dataField, 'notcontains', filterValue],
															'or',
															[field.dataField, '=', null],
														]
												}
											}

											switch (selectedFilterOperation) {
												case 'contains':
												case 'notcontains':
													filterValue = filterValue.replace(/\[/g, '[[]')
													break
											}

											if (field.dataType === 'uniqueidentifier') {
												return [field.dataField, selectedFilterOperation, filterValue]
											}

											if (gridRef.current && gridRef.current.instance) {
												const column = gridRef.current.instance.columnOption(field.dataField)
												if (column && column.defaultCalculateFilterExpression) {
													return column.defaultCalculateFilterExpression(
														filterValue,
														selectedFilterOperation,
														target
													)
												}
											}

											return [field.dataField, selectedFilterOperation, filterValue]
										}) as any
									}
									// link columns only hold an icon: always open narrow, whatever width was saved before
									width={isUrlField ? 100 : sizes[ndx] ?? undefined}
									alignment={isUrlField ? 'center' : undefined}
									minWidth={50}
									cssClass={isUrlField ? undefined : 'col-max'}
									cellRender={withHintText(
										field.dataField,
										clickablePopups[field.dataField]
											? renderClickablePopupCell({
													field,
													settings: clickablePopups[field.dataField],
													lookups,
													onOpen: popup => clickablePopupHost.current?.open(popup),
											  })
											: isMultipleLookupField
											? ({ data: rowData }: { data: Record<string, unknown> }) => (
													<MultipleSelectCellDisplay
														values={
															rowData[field.dataField] as Array<string | number> | string
														}
														lookupList={
															lookups[field.dataField] ?? dynamicLookups[field.dataField]
														}
													/>
											  )
											: lookups[field.dataField]
											? ({ data }) => {
													const value = data[field.dataField] as any
													const allValues = (filterLookups as any)[field.dataField]
													const lookup =
														allValues && !Array.isArray(allValues)
															? allValues
															: lookups[field.dataField]
													if (field.dataType === 'int' && Array.isArray(lookup)) {
														return value
													}
													const lookupValue = lookup[value]
													return lookupValue !== undefined ? lookupValue : value
											  }
											: dynamicLookups[field.dataField]
											? ({ data }) => {
													return data[field.dataField]
											  }
											: urls.indexOf(field.dataField as never) > -1
											? ({ data }) => {
													return renderUrl(data[field.dataField], 30, true, true)
											  }
											: clickableFields.indexOf(field.dataField as never) > -1
											? ({ data }) => {
													return (
														<a
															href={`#`}
															className="link"
															onClick={() => clickToField(field.dataField, data)}
														>
															{data[field.dataField]}
														</a>
													)
											  }
											: percentagesFields.indexOf(field.dataField as never) > -1
											? ({ data }) => {
													const num = parseFloat(data[field.dataField])
													return num % 1 === 0 ? num.toString() + ' %' : num.toFixed(2) + ' %'
											  }
											: badgeFields.indexOf(field.dataField) > -1
											? ({ data }) => {
													return <BadgeFieldsCellDisplay value={data[field.dataField]} />
											  }
											: isMultipleUploadField
											? ({ data }) => {
													const activeFolder =
														data[field.dataField] && data[field.dataField].length > 0
													return (
														<div
															onClick={() => {
																if (selectedRowRef.current) {
																	setShowMultipleUpload(field.dataField)
																}
															}}
															style={{
																cursor: 'pointer',
																display: 'flex',
																alignItems: 'center',
															}}
														>
															<span
																className={
																	activeFolder
																		? 'dx-icon-activefolder'
																		: 'dx-icon-inactivefolder'
																}
																style={{
																	fontSize: '18px',
																	fontWeight: activeFolder ? 'bold' : 'normal',
																	color: activeFolder ? 'black' : '#f0f0f0',
																}}
															></span>
														</div>
													)
											  }
											: isJsonField
											? ({ data }) => {
													const parsed = parseJsonValue(data[field.dataField])
													const count = Object.keys(parsed).length
													const active = count > 0
													return (
														<div
															onPointerDown={(ev: React.PointerEvent) => {
																ev.stopPropagation()
																selectedRowRef.current = data
																setShowJsonEditor(field.dataField)
															}}
															style={{
																cursor: 'pointer',
																display: 'flex',
																alignItems: 'center',
																gap: 6,
															}}
														>
															<span
																className="dx-icon-edit"
																style={{
																	fontSize: '18px',
																	fontWeight: active ? 'bold' : 'normal',
																	color: active ? 'black' : '#b0b0b0',
																}}
															/>
															{count > 0 ? (
																<span style={{ fontSize: 12, color: '#505050' }}>
																	{count} argument{count === 1 ? '' : 's'}
																</span>
															) : null}
														</div>
													)
											  }
											: undefined
									)}
								>
									{lookups[field.dataField] && !headerFilterAsTextCol && (
										<HeaderFilter
											allowSelectAll={false}
											search={{ enabled: true }}
											dataSource={filterStore(
												field.dataField,
												filterLookups[field.dataField] ?? lookups[field.dataField]
											)}
										/>
									)}
									{lookups[field.dataField] && !headerFilterAsTextCol && (
										<Lookup
											dataSource={lookupStore(lookups[field.dataField])}
											valueExpr="value"
											displayExpr="text"
										/>
									)}
									{(dynamicLookups[field.dataField] || dynamicFilterLookups[field.dataField]) &&
										!headerFilterAsTextCol && (
											<HeaderFilter
												allowSelectAll={false}
												search={{ enabled: true }}
												dataSource={headerDynamicLookups(field.dataField)}
											/>
										)}
									{dynamicLookups[field.dataField] && !headerFilterAsTextCol && (
										<Lookup
											allowClearing={true}
											dataSource={dynamicLookups[field.dataField]}
											valueExpr="value"
											displayExpr="text"
										/>
									)}
									{isMultipleUploadField && (
										<HeaderFilter
											allowSelectAll={false}
											dataSource={[
												{ text: 'Is blank', value: [field.dataField, '=', null] },
												{ text: 'Is not blank', value: [field.dataField, '<>', null] },
											]}
										/>
									)}
									{field.required && (
										<RequiredRule message={`${field.caption ?? field.dataField} is required`} />
									)}
								</Column>
							)
						})}
						{key && (
							<Summary>
								<GroupItem
									column={Array.isArray(key) ? key[0] : key}
									summaryType="count"
									displayFormat="{0} items"
								/>
							</Summary>
						)}
						<Toolbar>
							<Item name="groupPanel" />
							{buttons.map((button: ButtonConfig) => (
								<Item key={`button-${Math.random()}`} location="after" visible={button.visible ?? true}>
									<Button
										hint={button.text}
										icon={button.icon}
										disabled={
											editorMode ||
											(!button.doc &&
												!button.popup &&
												!button.export &&
												!button.execute &&
												totalCount === 0) ||
											(button.doc &&
												(Array.isArray(selectedRowRef.current) || docExportInProgress))
												? true
												: false
										}
										onClick={async () => {
											if (button.model) {
												if (gridRef.current && gridRef.current.instance) {
													handleClick(
														gridRef.current.instance.getSelectedRowsData(),
														button.model as string,
														button.filter
													)
												}
											} else if (button.filter) {
												const { field, operation, value } = button.filter
												setDefaultFilter(operation ? [field, operation, value] : '')
												setTimeout(() => {
													gridRef.current?.instance.columnOption(field, {
														filterValue: value,
														selectedFilterOperation: operation,
													})
													if (button.toggle) {
														toggleButton(button)
													}
												}, 100)
											} else if (button.url) {
												const url = button.url.replace(`{${key}}`, selectedRowRef.current[key])
												if (button.blank) {
													window.open(url, '_blank')
												} else {
													window.location.href = url
												}
											} else if (button.popup) {
												const isBulkAction = button.popup === 'BulkActionPopup'
												setButtonArgs({
													...(button.args || {}),
													...(isBulkAction && !button.args?.executeModel
														? { executeModel: model }
														: {}),
													...(isBulkAction && !button.args?.executeFolder
														? { executeFolder: folder }
														: {}),
													...(button.title != null && button.title !== ''
														? { title: button.title }
														: {}),
													popupType: button.popup,
												} as any)
												setButtonPopupShow(true)
											} else if (button.email) {
												const clickedAction = await confirmDialog(
													'Perform email send?',
													`You are going to send email with up ${totalCount} recipients.`
												)
												if (clickedAction) {
													const { field, check, subject, template, from, mailer } =
														button.email
													let url = `${API_FIBER_URL}/view/email?folder=${folder}&model=${model}&field=${field}&check=${check}&subject=${subject}`
													if (from) {
														url = `${url}&from=${from}`
													}
													if (template) {
														url = `${url}&template=${template}`
													}
													if (mailer) {
														url = `${url}&mailer=${mailer}`
													}
													if (dataFilter) {
														url = `${url}&filter=${dataFilter}`
													}
													axios
														.get(url)
														.then(response => response.data)
														.then(data => {
															notify(
																`The email was sent successfully to ${data.emails} recipients`,
																'success'
															)
															gridRef.current?.instance?.refresh()
														})
														.catch(error => {
															notify(`Email failed to send!`, 'error')
														})
												}
											} else if (button.doc) {
												if (docExportInProgress) {
													return
												}

												setDocExportInProgress(true)

												const { func } = button.doc
												let { filename } = button.doc
												const params: any = {
													folder,
													model,
													func,
													filename,
													filter: JSON.stringify(dataFilter),
													row: selectedRowRef.current,
												}
												if (!Array.isArray(key)) {
													filename = filename.replace('.docx', `_${params.row[key]}.docx`)
												}
												if (button.icon.indexOf('pdf') > -1) {
													params.type = 'pdf'
													filename = filename.replace('.docx', '.pdf')
												}
												trackDocExport(onProgress =>
													axios({
														method: 'post',
														url: `${API_FIBER_URL}/view/doc`,
														data: params,
														responseType: 'blob',
														onDownloadProgress: progressEvent =>
															onProgress(progressEvent.loaded || 0, progressEvent.total),
													})
												)
													.then(response => {
														const { data, headers } = response
														const blob = new Blob([data], { type: headers['content-type'] })
														if (headers['x-content-filename']) {
															filename = headers['x-content-filename']
														}
														saveAs(blob, filename)
													})
													.catch(() => {
														notify('Document export failed!', 'error')
													})
													.finally(() => {
														setDocExportInProgress(false)
													})
											} else if (button.export) {
												if (!selectedRowRef.current || Array.isArray(selectedRowRef.current)) {
													notify('Please select a single row to export.', 'warning')
													return
												}

												const { func } = button.export
												const params: any = {
													folder,
													model,
													func,
													row: selectedRowRef.current,
												}

												axios({
													method: 'post',
													url: `${API_FIBER_URL}/view/row-export`,
													data: params,
													responseType: 'blob',
												}).then(response => {
													const { data, headers } = response
													const blob = new Blob([data], { type: headers['content-type'] })
													let filename =
														headers['x-content-filename'] || `${model}_row_export.xlsx`
													saveAs(blob, filename)
												})
											} else if (button.execute) {
												const selectedKeys =
													gridRef.current?.instance?.getSelectedRowKeys?.() ?? []
												if (!selectedKeys.length) {
													notify('Select at least one row.', 'warning')
													return
												}
												try {
													await axios.post(`${API_FIBER_URL}/popup/execute`, {
														folder,
														model,
														execute: button.execute,
														selected_keys: selectedKeys,
													})
													notify(button.successMessage || `${button.text} done.`, 'success')
													gridRef.current?.instance?.refresh()
													gridRef.current?.instance?.clearSelection()
												} catch (error: any) {
													notify(
														error.response?.data?.message ||
															error.message ||
															'Action failed',
														'error'
													)
												}
											} else if (button.addRow) {
												const src = selectedRowRef.current as Record<string, unknown> | null
												if (!src || Array.isArray(src)) {
													notify('Select a row first.', 'warning')
													return
												}
												duplicateInitRef.current = {
													...Object.fromEntries(
														(button.addRow.copy ?? [])
															.filter((field: string) => field in src)
															.map((field: string) => [field, src[field]])
													),
													...(button.addRow.values ?? {}),
												}
												setPopupTitle(button.title ?? null)
												if (popupInsertOnly) {
													setEditMode('popup')
												}
												gridRef.current?.instance?.getScrollable()?.scrollTo({ top: 0 })
												setTimeout(() => gridRef.current?.instance?.addRow(), 400)
											} else if (button.exec) {
												axios({
													method: 'post',
													url: `${API_FIBER_URL}/view/exec`,
													data: button.exec,
												}).then(response => {
													notify(`${button.text} executed successfully`, 'info')
												})
											}
										}}
									/>
								</Item>
							))}
							<Item name="applyFilterButton" cssClass="applyFilter" />
							<Item location="after">
								<Button icon="bookmark" hint="Create Filter" onClick={openFilterPopup} />
							</Item>
							<Item location="after">
								<Button
									icon="revert"
									hint="Clear Filter"
									onClick={() => {
										gridRef.current?.instance?.clearFilter()
										gridRef.current?.instance?.filter(defaultFilter)
									}}
								/>
							</Item>
							<Item location="after">
								<Button
									icon="refresh"
									hint="Refresh"
									disabled={editorMode ? true : false}
									onClick={() => {
										gridRef.current?.instance?.refresh()
									}}
								/>
							</Item>
							<Item location="after">
								<Button
									icon="edit"
									hint="Edit Mode"
									disabled={!updating}
									onClick={async () => {
										if (editorMode) {
											const clickedAction = await confirmDialog(
												'Confirm Exit from Edit Mode',
												`Exiting edit mode will discard changes made to ${changesRef.current.length} rows. Are you sure you want to proceed?`
											)
											if (clickedAction) {
												setEditorMode(editorMode => !editorMode)
											}
										} else {
											setEditorMode(editorMode => !editorMode)
										}
									}}
								/>
							</Item>
							<Item location="after">
								<Button
									icon="save"
									hint="Save"
									className={editorMode ? 'flashButton' : undefined}
									disabled={!updating || !editorMode || isSaving}
									onClick={handleSaveData}
								/>
							</Item>
							{popupInsertOnly ? (
								<Item location="after">
									<Button
										icon="add"
										hint="Add a row"
										onClick={() => {
											setEditMode('popup')
											const g = gridRef.current?.instance
											if (g) g.getScrollable?.()?.scrollTo?.(0)
											setTimeout(() => gridRef.current?.instance?.addRow(), 100)
										}}
									/>
								</Item>
							) : (
								<Item name="addRowButton" />
							)}
							{duplicating && (
								<Item location="after">
									<Button
										icon="copy"
										hint="Duplicate"
										disabled={Array.isArray(selectedRowRef.current)}
										onClick={() => {
											if (popupInsertOnly) {
												setEditMode('popup')
												gridRef.current?.instance?.getScrollable?.()?.scrollTo?.(0)
											}
											handleDuplicate()
										}}
									/>
								</Item>
							)}
							<Item
								name="exportButton"
								disabled={editorMode}
								cssClass={exportLockedTime !== null ? 'dx-state-off' : undefined}
							/>
							<Item name="columnChooserButton" />
							<Item location="after">
								<Button
									icon="detailslayout"
									hint="Row Detail"
									disabled={
										editorMode || Array.isArray(selectedRowRef.current) || totalCount === 0
											? true
											: false
									}
									onClick={() => {
										setShowRow(true)
									}}
								/>
							</Item>
							{map.fields && (
								<Item location="after">
									<Button
										icon="map"
										hint="Map"
										onClick={() => {
											setShowMap(!showMap)
										}}
									/>
								</Item>
							)}
						</Toolbar>
						{detail.model && <MasterDetail enabled={true} component={DetailSection} />}
						<Export
							enabled={canExportView(user, folder || '', model || '')}
							texts={{ exportAll: 'You can make one export per minute' }}
						/>
					</DataGrid>
					<div
						ref={autoFillOverlayRef}
						style={{
							display: 'none',
							position: 'fixed',
							inset: 0,
							zIndex: 20000,
							background: 'rgba(255,255,255,0.55)',
							pointerEvents: 'auto',
						}}
					>
						<div
							style={{
								position: 'absolute',
								top: '50%',
								left: '50%',
								transform: 'translate(-50%, -50%)',
								color: '#333',
								fontWeight: 600,
							}}
						>
							Auto filling fields, please wait...
						</div>
					</div>
				</div>
			)}
			{map.fields && key && (
				<Map
					folder={folder}
					model={model}
					filter={dataFilter}
					title={map.title || null}
					settings={map.settings}
					visible={showMap}
					selected={!Array.isArray(key) ? selectedRow[key] : null}
					totalCount={totalCount}
					onClose={handleMapPopupClose}
					fields={fields}
					urls={urls}
					valueColors={valueColors}
				/>
			)}
			{showRow && (
				<Row
					fields={fields}
					urls={urls}
					values={selectedRowRef.current}
					lookups={lookups}
					model={model}
					onHide={() => setShowRow(false)}
				/>
			)}
			{showClickableField && (
				<ClickableField
					model={model}
					folder={folder}
					field={showClickableField.field}
					data={showClickableField.data}
					onHide={() => setShowClickableField(null)}
				/>
			)}
			<ClickablePopupHost
				ref={clickablePopupHost}
				model={model}
				folder={folder}
				onChanged={() => gridRef.current?.instance?.refresh()}
			/>
			{showAuditTrail && (
				<AuditTrail
					visible={showAuditTrail}
					pKey={key}
					dataField={auditField}
					folder={folder}
					model={model}
					data={auditData}
					onHide={() => setShowAuditTrail(false)}
				/>
			)}
			{showMultipleUpload !== '' && selectedRowRef.current && (
				<MultipleUpload
					pKey={key}
					folder={folder}
					model={model}
					name={showMultipleUpload}
					values={selectedRowRef.current}
					canEdit={allowEditing(showMultipleUpload, true)}
					settings={multipleUploadFields[showMultipleUpload as keyof typeof multipleUploadFields]}
					onHide={() => setShowMultipleUpload('')}
				/>
			)}
			{showJsonEditor !== '' && selectedRowRef.current && (
				<JsonEditor
					pKey={key}
					folder={folder!}
					model={model!}
					name={showJsonEditor}
					values={selectedRowRef.current}
					allowSave={allowEditing(
						showJsonEditor,
						(fields as any[]).find(f => f.dataField === showJsonEditor)?.allowEditing
					)}
					onHide={() => setShowJsonEditor('')}
				/>
			)}
			<Popup
				visible={buttonPopupShow}
				showTitle={false}
				contentRender={renderButtonPopContent}
				position="center"
				resizeEnabled={(buttonArgs as any)?.popupType !== 'ModelGridPopup'}
				width={(buttonArgs as any)?.popupType === 'ModelGridPopup' ? 780 : undefined}
				height={(buttonArgs as any)?.popupType === 'ModelGridPopup' ? 620 : undefined}
				minWidth={400}
				minHeight={300}
				onHiding={() => {
					setButtonPopupShow(false)
					const popupType = (buttonArgs as any)?.popupType
					if (popupType === 'BulkActionPopup') {
						gridRef.current?.instance?.refresh()
						return
					}
					if (popupType !== 'ModelPopup' || !model) return
					axios
						.get(`${API_FIBER_URL}/view/fields?folder=${folder || ''}&model=${model}`)
						.then(r => r.data.data)
						.then(d => {
							setLookups(d.lookups)
							setFilterLookups(d.filterLookups)
							if (d.dynamicFilterLookups) setDynamicFilterLookups(d.dynamicFilterLookups)
							Object.keys(d.dynamicLookups || {}).forEach(k => {
								if (!multipleLookups.includes(k)) loadDynamicLookup(k)
							})
						})
						.finally(() => gridRef.current?.instance?.refresh())
				}}
				hideOnOutsideClick={true}
			/>
			<SaveFilterPopup
				visible={filterPopupVisible}
				onClose={() => setFilterPopupVisible(false)}
				onApply={handleApplyFilter}
				modelName={model}
				gridInstance={gridRef.current?.instance}
			/>
			{hotkeyHelpModal}
		</React.Fragment>
	)
}
